import { useTenantContext } from './TenantProvider';

/**
 * Hook for accessing tenant information
 *
 * @example
 * ```tsx
 * const { tenant, settings, isHost } = useTenant();
 *
 * // Show tenant-specific logo
 * {settings.logoUrl && <img src={settings.logoUrl} alt="Logo" />}
 *
 * // Check if on host
 * {isHost ? <HostContent /> : <TenantContent />}
 * ```
 */
export const useTenant = () => {
  const { tenant, settings, isLoading, isHost, tenantName } = useTenantContext();

  return {
    /**
     * Current tenant info (id, name, isActive)
     */
    tenant,
    /**
     * Tenant-specific settings (logo, theme, CSS)
     */
    settings,
    /**
     * Whether tenant data is loading
     */
    isLoading,
    /**
     * Whether this is the host (no tenant subdomain)
     */
    isHost,
    /**
     * Tenant name extracted from subdomain
     */
    tenantName,
    /**
     * Tenant ID (convenience accessor)
     */
    tenantId: tenant?.id,
    /**
     * Whether tenant is active
     */
    isActive: tenant?.isActive ?? true,
    /**
     * Tenant's custom logo URL
     */
    logoUrl: settings?.logoUrl,
    /**
     * Tenant's company name
     */
    companyName: settings?.companyName,
  };
};

export default useTenant;
